import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  Image,
  ListItem,
  Spinner,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import useIsLoggedIn from "core/features/auth/hooks/useIsLoggedIn";
import useEventoDetalhado from "core/features/eventos/hooks/useEventoDetalhado";
import useGetSetorOpcoes from "core/features/eventos/hooks/useGetSetorOpcoes";
import { Evento, EventoSetorOpcao } from "core/features/eventos/types";
import colors from "core/resources/theme/colors";
import moment from "moment";
import { useFieldArray, useForm } from "react-hook-form";
import { BiChevronLeft } from "react-icons/bi";
import { MdEvent, MdShoppingCart } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import OutlineButton from "ui/components/Button/OutlineButton";
import Container from "ui/components/Layout/Container";
import BarStep from "../components/BarStep";
import CardIngresso from "../components/DataVisita/components/CardIngresso";
import useBackToBeginning from "../hooks/useBackToBeginning";
import useCurrentStep from "../hooks/useCurrentStep";
import { useIngressosStore } from "../hooks/useIngressosStore";
import useStepTitle from "../hooks/useStepTitle";
import IngressoContainer from "./IngressoContainer";
moment.locale("pt-br");

function ComprarIngressos() {
  const { calendarioId } = useParams<{
    calendarioId: string;
  }>();
  useBackToBeginning();

  const activeStep = useCurrentStep();
  const [titleStep, titleNextStep] = useStepTitle(activeStep, activeStep + 1);
  const dataSelecionada = useIngressosStore((state) => state.dataSelecionada);
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(1);
  const {
    watch,
    setValue,
    getValues,
    control,
    setError,
    clearErrors,
    formState,
    trigger,
  } = useForm<{
    items: { id: number; quantity: number; item: EventoSetorOpcao }[];
    termo: boolean;
  }>({
    defaultValues: {
      items: [],
    },
  });
  const valorTotal = Object.entries(watch("items")).reduce((acc, curr) => {
    const [, value] = curr;
    return acc + value.quantity * value.item.valorBase;
  }, 0);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });
  const eventoId = useIngressosStore((state) => state.eventoId);
  const setEventoId = useIngressosStore((state) => state.setEventoId);

  const { data: eventos } = useEventoDetalhado();

  const eventoSelecionado = useMemo(
    () => eventos?.find((evento) => evento.id === eventoId),
    [eventos, eventoId]
  );

  const { isLoading } = useGetSetorOpcoes((items) => {
    items?.forEach((item) => {
      append({ id: item.id, quantity: 0, item });
    });
  });

  const updateItemQuantity = (itemIndex: number, newQuantity: number) => {
    setValue(
      `items.${itemIndex}.quantity` as `items.${number}.quantity`,
      newQuantity
    );
  };

  useEffect(() => {
    if (eventos && eventos.length === 1) {
      setEventoId(eventos[0].id);
    }
  }, [eventos, setEventoId]);

  const isLogged = useIsLoggedIn();
  return (
    <Container>
      <Flex flexDir="column">
        <Flex
          pt={["130px", "130px", "130px", "330px"]}
          mb="80px"
          gap="44px"
          flexDir={"column"}
        >
          <BarStep
            step={activeStep}
            titleNextStep={titleNextStep!}
            nextStep={activeStep + 1}
            titleStep={titleStep!}
          />
          <EscolherIngresso
            formStep={formStep}
            setFormStep={setFormStep}
            eventoSelecionado={eventoSelecionado}
            isLoading={isLoading}
            eventos={eventos}
            dataSelecionada={dataSelecionada}
            fields={fields}
            getValues={getValues}
            updateItemQuantity={updateItemQuantity}
            valorTotal={valorTotal}
            formState={formState}
            trigger={trigger}
            setError={setError}
            clearErrors={clearErrors}
            isLogged={isLogged}
            calendarioId={calendarioId}
            control={control}
          />
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center">
        <OutlineButton
          title="VOLTAR"
          color={colors.blue}
          prevIcon={<BiChevronLeft size={20} />}
          onClick={() => {
            if (formStep === 1) {
              navigate(-1);
              remove();
            } else {
              setFormStep(1);
              setEventoId(undefined);
              remove();
            }
          }}
        />
      </Flex>
    </Container>
  );
}

const fmtReal = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
}).format;

export default ComprarIngressos;

export function EscolhaEvento({
  setStep,
  eventos,
  isLoading,
}: {
  setStep: any;
  eventos: Evento[];
  isLoading?: boolean;
}) {
  const setEventoId = useIngressosStore((s) => s.setEventoId);

  const handleEscolherEvento = (id: string) => {
    setEventoId(id);
    setStep(2);
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color={colors.blue} />
      </Center>
    );
  }

  if (!eventos || eventos.length === 0) {
    return (
      <Center py={20}>
        <Text fontSize="lg" color="gray.500">
          Nenhum evento encontrado.
        </Text>
      </Center>
    );
  }

  return (
    <Box px={4} maxW="1000px" mx="auto">
      <Text fontSize="3xl" fontWeight="bold" color={colors.purple500} mb={8}>
        Selecione um Evento
      </Text>

      <Stack spacing={8}>
        {eventos.map((evento) => (
          <Flex
            key={evento.id}
            borderWidth="1px"
            borderRadius="xl"
            overflow="hidden"
            bg="white"
            boxShadow="lg"
            direction={{ base: "column", md: "row" }}
          >
            {evento.imagemUrl && (
              <Image
                src={evento.imagemUrl}
                alt={evento.nome}
                width={{ base: "100%", md: "300px" }}
                height="100%"
                objectFit="contain"
                bg="gray.50"
              />
            )}

            <Box p={6} flex="1">
              <Flex
                justify="space-between"
                align="center"
                mb={2}
                flexWrap="wrap"
              >
                <Flex align="center" gap={2}>
                  <MdEvent color={colors.purple500} size={24} />
                  <Text
                    fontSize="xl"
                    fontWeight="semibold"
                    color={colors.purple500}
                  >
                    {evento.nome}
                  </Text>
                </Flex>
                <Text
                  fontSize="sm"
                  color={colors.gray600}
                  mt={{ base: 2, md: 0 }}
                >
                  {moment(evento.dataEvento).format("DD [de] MMMM [às] HH:mm")}
                </Text>
              </Flex>

              <Text fontSize="md" color={colors.gray600} mb={6}>
                {evento.texto}
              </Text>

              <Flex justify="flex-end">
                <Button
                  onClick={() => handleEscolherEvento(evento.id)}
                  bg={colors.newBlue}
                  color="white"
                  fontWeight="bold"
                  _hover={{ bg: "#cc1e1e" }}
                  px={6}
                  py={2}
                  borderRadius="md"
                >
                  Escolher
                </Button>
              </Flex>
            </Box>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}

function EscolherIngresso({
  formStep,
  setFormStep,
  eventoSelecionado,
  isLoading,
  eventos,
  dataSelecionada,
  fields,
  getValues,
  updateItemQuantity,
  valorTotal,
  formState,
  trigger,
  setError,
  clearErrors,
  isLogged,
  calendarioId,
  control,
}: {
  formStep: number;
  setFormStep: (step: number) => void;
  eventoSelecionado?: Evento;
  isLoading: boolean;
  eventos?: Evento[];
  dataSelecionada?: string;
  fields: any[];
  getValues: any;
  updateItemQuantity: (index: number, value: number) => void;
  valorTotal: number;
  formState: any;
  trigger: any;
  setError: any;
  clearErrors: any;
  isLogged: boolean;
  calendarioId?: string;
  control: any;
}) {
  const navigate = useNavigate();

  if (formStep === 1 && !eventoSelecionado) {
    return (
      <EscolhaEvento
        setStep={setFormStep}
        eventos={eventos ?? []}
        isLoading={isLoading}
      />
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Text
          fontWeight="medium"
          fontSize="28px"
          color={colors.purple500}
          flexWrap="wrap"
          textAlign={"center"}
        >
          {eventoSelecionado?.nome}
        </Text>
        <Text fontSize="16px">{eventoSelecionado?.texto}</Text>
      </Stack>

      <FormControl isInvalid={formState.errors.items?.type === "required"}>
        <FormErrorMessage mb={2}>
          Você deve selecionar pelo menos um ingresso
        </FormErrorMessage>

        <Stack spacing={0}>
          <IngressoContainer
            title={`Ingressos para visita em ${moment(dataSelecionada).format(
              "DD [de] MMMM [de] YYYY"
            )}`}
          >
            {isLoading ? (
              <Center h="160px">
                <Spinner size="xl" color={colors.blue} />
              </Center>
            ) : null}

            {fields?.map((field, i) => (
              <CardIngresso
                imagemUrl={field.item?.imagemUrl ?? ""}
                quantity={getValues(`items.${i}.quantity`)}
                addQuantity={() => {
                  const currentTotal = getValues().items[i].quantity || 0;
                  updateItemQuantity(i, currentTotal + 1);
                }}
                removeQuantity={() => {
                  const currentTotal = getValues().items[i].quantity || 0;
                  if (currentTotal > 0) {
                    updateItemQuantity(i, currentTotal - 1);
                  }
                }}
                key={field.id}
                titleCard={field.item.nome}
                valorIngresso={
                  field.item.valorBase +
                  (field.item.valorTaxaWeb > 0 ? field.item.valorTaxaWeb : 0)
                }
                valorTotalIngresso={
                  field.item.valorBase + field.item.valorTaxaWeb
                }
                id={field.id}
                textoInfo={field.item.textoInfo}
              />
            ))}
          </IngressoContainer>

          <Flex
            w="100%"
            justify="space-between"
            h="80px"
            align="center"
            roundedBottom="16px"
            px="20px"
            boxShadow="base"
          >
            <Flex gap={4}>
              <MdShoppingCart size={30} color={colors.newBlue} />
              <Text fontSize="24px" fontWeight="bold" color={colors.newBlue}>
                {fmtReal(valorTotal)}
              </Text>
            </Flex>
            <Flex>
              <OutlineButton
                title="PRÓXIMO"
                color={colors.white}
                bg={colors.newBlue}
                bgBorder={colors.newBlue}
                onClick={async () => {
                  const hasTickets = getValues().items.some((item: any) => item.quantity > 0);
                  if (!hasTickets) {
                    setError("items", { type: "required" });
                    return;
                  }

                  clearErrors("items");
                  useIngressosStore.getState().setIngressos(getValues().items);

                  if (isLogged) {
                    navigate(`/ingresso/${calendarioId}/pagamento`);
                  } else {
                    navigate(`/ingresso/${calendarioId}/identificacao`);
                  }
                }}
              />
            </Flex>
          </Flex>

          <Box pt={4} px={4}>
            <Text fontSize="16px" color={colors.gray500}>
              Informação das idades quanto aos valores dos ingressos.
            </Text>
            <UnorderedList fontSize="16px" color={colors.gray500} px={2}>
              <ListItem>
                0 a 5 anos não paga ingresso (obrigatória apresentação do
                documento na portaria para liberação)
              </ListItem>
              <ListItem>
                6 a 10 anos paga pelo ingresso “INFANTIL 6 A 10 ANOS”
                (obrigatória apresentação de documento na portaria para
                validação do ingresso)
              </ListItem>
              <ListItem>
                A partir de 11 anos paga pelo ingresso “INTEIRO”
              </ListItem>
              <ListItem>
                Certifique-se da aprovação da compra do seu ingresso!
              </ListItem>
            </UnorderedList>
          </Box>
        </Stack>
      </FormControl>
    </>
  );
}
